import { useQuery } from "@apollo/client";
import { TFunction } from "i18next";
import _, { chain, uniq } from "lodash";
import { useMemo } from "react";

import { UnitMass } from "components/AssessmentStats/types";
import { ChartDisplayType } from "glue/Chart/chart.constants";
import { formatValue } from "glue/Chart/components/ChartTooltip";
import {
    CrremPathway,
    GetAssetRiskQuery,
    GetAssetRiskQueryVariables,
} from "graphql-types/graphql";
import { NextProperty } from "legacyNext/types";

import { GET_ASSET_RISK } from "./asset-risk.query";
import { AssetRiskProperty, AssetRiskSeries } from "./asset-risk.types";

export function assetRiskListToChartSeries({
    property,
    crremPathways,
    type,
    t,
}: {
    property: AssetRiskProperty;
    crremPathways?: CrremPathway[];
    type: ChartDisplayType;
    t: TFunction;
}): AssetRiskSeries {
    const { id, constructionYear, fullAddress, crremPropertyType } = property;

    const intensity = property.summary.map(
        ({ int, proj }) => (int ?? 0) + Math.max(0, (proj ?? 0) - (int ?? 0))
    );

    const color =
        crremPathways &&
        crremPathways.find(
            ({ crremCategory }) => crremCategory === crremPropertyType
        )?.color;

    const stranded = property.summary.map((item) => {
        const pathway = crremPathways?.find(
            (crrem) => crrem.year === item.date.getFullYear()
        );

        if (!pathway || !item.int) return false;

        return item.int > pathway.value;
    });

    return {
        id: `property-${id}`,
        label: crremPropertyType ?? undefined,
        constructionYear,
        totalArea: property.area ?? undefined,
        assetExternalId: property.assetExternalId,
        fullAddress,
        data: intensity,
        type: "line",
        curve: "linear",
        stranded,
        color: color || "black",
        valueFormatter: (value: any) =>
            formatValue(value, type, t, true, UnitMass.KILOGRAM) || "",
    };
}

export function crremPathwaysToChartSeries(
    crremPathways: CrremPathway[],
    type: ChartDisplayType
): AssetRiskSeries[] {
    const typeUnit = type === ChartDisplayType.EMISSIONS ? "kgCO2e" : "kWh";
    return _.chain(crremPathways)
        .filter((pathway) => pathway.unit.includes(typeUnit))
        .groupBy((pathway) => pathway.crremCategory)
        .map((pathways): AssetRiskSeries => {
            const category = pathways[0].crremCategory;

            return {
                id: "crrem-" + category,
                label: category,
                data: pathways.map((pathway) => pathway.value),
                type: "line",
                curve: "natural",
                color: pathways[0].color,
                showMark: false,
            };
        })
        .value();
}

export const useAssetRiskChartData = (
    propertyList: NextProperty[],
    key: ChartDisplayType,
    t: TFunction
): {
    loading: boolean;
    chartData: AssetRiskSeries[];
    pathways: CrremPathway[];
} => {
    const { data, loading } = useQuery<
        GetAssetRiskQuery,
        GetAssetRiskQueryVariables
    >(GET_ASSET_RISK, {
        variables: {
            propertyIds: propertyList.map((propery) => propery.id),
            crremCategories: uniq(
                propertyList.flatMap((property) => [
                    property.crremPropertyType,
                    ...property.crremPropertyTypes,
                ])
            ),
        },
    });

    const crremTypeUnit = key === ChartDisplayType.EMISSIONS ? "kgCO2e" : "kWh";
    const crremPathways = useMemo(
        () =>
            data?.getCrremPathways.filter((pathway) =>
                pathway.unit.includes(crremTypeUnit)
            ) ?? [],
        [crremTypeUnit, data?.getCrremPathways]
    );

    const chartData = useMemo(() => {
        const intensityKey =
            key === ChartDisplayType.EMISSIONS
                ? "emissionIntensity"
                : "energyIntensity";
        const projectedKey =
            key === ChartDisplayType.EMISSIONS
                ? "projectedEmissionIntensity"
                : "projectedEnergyIntensity";

        const propertiesSeries = propertyList.map((property) => {
            const asset = data?.getAssetRiskChartData.find(
                (asset) => asset.propertyId === property.id
            );

            const summary = chain(asset?.summary)
                .sortBy("year")
                .map((item) => {
                    const int = item[intensityKey] ?? 0;
                    const proj = item[projectedKey] ?? 0;

                    return {
                        date: new Date(item.year.toString()),
                        int:
                            key === ChartDisplayType.EMISSIONS
                                ? int / 1000
                                : int ?? null,
                        proj:
                            key === ChartDisplayType.EMISSIONS
                                ? proj / 1000
                                : proj ?? null,
                    };
                })
                .value();

            return assetRiskListToChartSeries({
                property: { ...property, summary },
                crremPathways,
                type: key,
                t,
            });
        });

        const crremPathwaysSeries = crremPathways
            ? crremPathwaysToChartSeries(crremPathways, key)
            : [];

        return [...propertiesSeries, ...crremPathwaysSeries];
    }, [data, key, propertyList, crremPathways, t]);

    return {
        chartData,
        loading,
        pathways: crremPathways,
    };
};
